import { Helmet } from 'react-helmet-async'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useState } from 'react'
import { supabase, storage } from '../lib/supabase'
import { useRecaptcha } from '../hooks/useRecaptcha'

// Validation schema
const arcoSchema = z.object({
  // Titular
  titular_nombres: z.string().min(2, 'Los nombres son requeridos'),
  titular_apellidos: z.string().min(2, 'Los apellidos son requeridos'),
  titular_email: z.string().email('Ingrese un email válido'),
  titular_domicilio: z.string().min(5, 'El domicilio es requerido'),
  titular_documento: z.string().min(8, 'El documento es requerido'),
  es_representante: z.boolean(),
  
  // Representante (condicional)
  representante_nombres: z.string().optional(),
  representante_apellidos: z.string().optional(),
  representante_documento: z.string().optional(),
  
  // Solicitud
  tipo_solicitud: z.enum(['Acceso', 'Rectificación', 'Cancelación', 'Oposición']),
  detalle_solicitud: z.string().min(20, 'Por favor detalle su solicitud (mínimo 20 caracteres)'),
}).refine((data) => {
  if (data.es_representante) {
    return data.representante_nombres && data.representante_apellidos && data.representante_documento
  }
  return true
}, {
  message: "Complete los datos del representante",
  path: ["representante_nombres"],
})

export default function DerechosARCO() {
  const { verifyRecaptcha } = useRecaptcha()
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)
  const [folio, setFolio] = useState(null)
  
  const [titularDNIFile, setTitularDNIFile] = useState(null)
  const [representanteDNIFile, setRepresentanteDNIFile] = useState(null)
  const [representanteAcreditacionFile, setRepresentanteAcreditacionFile] = useState(null)
  const [documentosAdicionalesFile, setDocumentosAdicionalesFile] = useState(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm({
    resolver: zodResolver(arcoSchema),
    defaultValues: {
      es_representante: false,
      tipo_solicitud: 'Acceso'
    }
  })

  const esRepresentante = watch('es_representante')

  const onSubmit = async (data) => {
    setLoading(true)
    setError(null)

    try {
      // Verify reCAPTCHA
      const recaptchaResult = await verifyRecaptcha('derechos_arco')
      if (!recaptchaResult.success) {
        throw new Error('Error de verificación. Por favor intenta de nuevo.')
      }

      // Preparar datos para inserción
      const submitData = { ...data }

      // Subir archivo DNI del titular si existe
      if (titularDNIFile) {
        const path = `titular_dni/${Date.now()}_${titularDNIFile.name.replace(/\s+/g, '_')}`
        const { error: uploadError } = await storage.uploadImage('arco', titularDNIFile, path)
        if (uploadError) throw uploadError
        submitData.titular_dni_url = path
      }

      // Subir archivos del representante si es representante
      if (data.es_representante) {
        if (representanteDNIFile) {
          const path = `representante_dni/${Date.now()}_${representanteDNIFile.name.replace(/\s+/g, '_')}`
          const { error: uploadError } = await storage.uploadImage('arco', representanteDNIFile, path)
          if (uploadError) throw uploadError
          submitData.representante_dni_url = path
        }

        if (representanteAcreditacionFile) {
          const path = `acreditacion/${Date.now()}_${representanteAcreditacionFile.name.replace(/\s+/g, '_')}`
          const { error: uploadError } = await storage.uploadImage('arco', representanteAcreditacionFile, path)
          if (uploadError) throw uploadError
          submitData.representante_acreditacion_url = path
        }
      }

      // Subir documentos adicionales si existen
      if (documentosAdicionalesFile) {
        const path = `adicionales/${Date.now()}_${documentosAdicionalesFile.name.replace(/\s+/g, '_')}`
        const { error: uploadError } = await storage.uploadImage('arco', documentosAdicionalesFile, path)
        if (uploadError) throw uploadError
        submitData.documentos_adicionales_url = path
      }

      // Insertar en Supabase
      const { data: result, error: submitError } = await supabase
        .from('solicitudes_arco')
        .insert([submitData])
        .select('folio')
        .single()

      if (submitError) throw submitError

      setFolio(result.folio)
      setSuccess(true)
      reset()
      setTitularDNIFile(null)
      setRepresentanteDNIFile(null)
      setRepresentanteAcreditacionFile(null)
      setDocumentosAdicionalesFile(null)
      
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err) {
      console.error('Error al enviar solicitud:', err)
      setError('Ocurrió un error al enviar la solicitud. Por favor, intente nuevamente.')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <>
        <Helmet>
          <title>Derechos ARCO - Plaza Aurora</title>
        </Helmet>
        <div style={{
          maxWidth: 'var(--container-max-width)',
          margin: '0 auto',
          padding: '0 2rem'
        }}>
          <div style={{ maxWidth: '672px', margin: '0 auto' }}>
            <div style={{
              background: 'linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)',
              border: '1px solid #6EE7B7',
              borderRadius: 'var(--radius-md)',
              padding: '2rem',
              textAlign: 'center'
            }}>
              <div style={{ color: '#059669', marginBottom: '1rem' }}>
                <svg style={{ width: '64px', height: '64px', margin: '0 auto' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 style={{
                fontSize: 'var(--font-size-h2)',
                fontWeight: '700',
                fontFamily: 'var(--font-display)',
                color: '#065F46',
                marginBottom: '0.5rem'
              }}>¡Solicitud Registrada Exitosamente!</h2>
              {folio && (
                <p style={{
                  fontSize: 'var(--font-size-h3)',
                  fontFamily: 'var(--font-ui)',
                  color: '#065F46',
                  marginBottom: '1rem'
                }}>
                  Su número de folio es: <span style={{ fontWeight: '700' }}>{folio}</span>
                </p>
              )}
              <p style={{
                fontSize: 'var(--font-size-body)',
                fontFamily: 'var(--font-ui)',
                color: '#065F46',
                marginBottom: '1.5rem'
              }}>
                Hemos recibido su solicitud de Derechos ARCO. Le responderemos a la brevedad al correo electrónico proporcionado.
              </p>
              <button
                onClick={() => setSuccess(false)}
                style={{
                  padding: '0.875rem 2rem',
                  background: 'var(--ctaBg)',
                  color: 'var(--ctaText)',
                  border: 'none',
                  borderRadius: 'var(--radius-pill)',
                  fontSize: 'var(--font-size-body)',
                  fontWeight: '600',
                  fontFamily: 'var(--font-ui)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)'
                  e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                  e.currentTarget.style.background = 'var(--brand-blue)'
                  e.currentTarget.style.color = 'white'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = 'none'
                  e.currentTarget.style.background = 'var(--ctaBg)'
                  e.currentTarget.style.color = 'var(--ctaText)'
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-400)'
                  e.currentTarget.style.background = 'var(--brand-blue-600)'
                }}
              >
                Registrar Otra Solicitud
              </button>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>Derechos ARCO - Plaza Aurora</title>
      </Helmet>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '4rem 2rem'
      }}>
        <div style={{ maxWidth: '896px', margin: '0 auto' }}>
          {/* Header */}
          <div style={{ marginBottom: '2rem' }}>
            <h1 style={{
              fontSize: 'var(--h1)',
              fontWeight: '700',
              fontFamily: 'var(--font-display)',
              color: 'var(--color-text-primary)',
              marginBottom: '1.5rem'
            }}>Derechos ARCO</h1>
            
            <div style={{
              background: '#F3F4F6',
              padding: '1.5rem',
              borderRadius: 'var(--radius-md)',
              marginBottom: '2rem',
              border: '1px solid #E5E7EB'
            }}>
              <p style={{
                fontSize: 'var(--font-size-body)',
                fontFamily: 'var(--font-ui)',
                color: 'var(--color-text-primary)',
                lineHeight: '1.6',
                margin: 0
              }}>
                GRUPO SOLHER S.A. (en adelante "PLAZA AURORA") en cumplimiento de lo dispuesto por la Ley No. 29733, Ley de Protección de Datos Personales, y su Reglamento, aprobado por Decreto Supremo No. 003-2013-JUS, pone a su disposición el procedimiento para el ejercicio de los derechos de Acceso, Rectificación, Cancelación y Oposición respecto de sus Datos Personales contenidos en los bancos de datos de nuestra titularidad.
              </p>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div style={{
              background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)',
              border: '1px solid #F87171',
              borderRadius: 'var(--radius-md)',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              <p style={{
                fontSize: 'var(--font-size-body)',
                fontFamily: 'var(--font-ui)',
                color: '#991B1B'
              }}>{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Sección 1: Datos del Titular */}
            <div className="libro-card" style={{ marginBottom: '2rem' }}>
              <h2 style={{
                fontSize: 'var(--font-size-h2)',
                fontWeight: '700',
                fontFamily: 'var(--font-display)',
                color: '#111827',
                marginBottom: '1.5rem'
              }}>Datos del Titular de los Datos Personales</h2>
              
              <div className="arco-grid-2col">
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--sm)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Nombres *
                  </label>
                  <input
                    type="text"
                    {...register('titular_nombres')}
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #D1D5DB',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--font-size-body)',
                      fontFamily: 'var(--font-ui)'
                    }}
                  />
                  {errors.titular_nombres && (
                    <p style={{ fontSize: 'var(--sm)', color: '#ef4444', marginTop: '0.25rem' }}>
                      {errors.titular_nombres.message}
                    </p>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--sm)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Apellidos *
                  </label>
                  <input
                    type="text"
                    {...register('titular_apellidos')}
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #D1D5DB',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--font-size-body)',
                      fontFamily: 'var(--font-ui)'
                    }}
                  />
                  {errors.titular_apellidos && (
                    <p style={{ fontSize: 'var(--sm)', color: '#ef4444', marginTop: '0.25rem' }}>
                      {errors.titular_apellidos.message}
                    </p>
                  )}
                </div>

                <div className="arco-full-width">
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--sm)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Correo Electrónico *
                  </label>
                  <input
                    type="email"
                    {...register('titular_email')}
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #D1D5DB',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--font-size-body)',
                      fontFamily: 'var(--font-ui)'
                    }}
                  />
                  {errors.titular_email && (
                    <p style={{ fontSize: 'var(--sm)', color: '#ef4444', marginTop: '0.25rem' }}>
                      {errors.titular_email.message}
                    </p>
                  )}
                </div>

                <div className="arco-full-width">
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--sm)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Domicilio *
                  </label>
                  <input
                    type="text"
                    {...register('titular_domicilio')}
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #D1D5DB',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--font-size-body)',
                      fontFamily: 'var(--font-ui)'
                    }}
                  />
                  {errors.titular_domicilio && (
                    <p style={{ fontSize: 'var(--sm)', color: '#ef4444', marginTop: '0.25rem' }}>
                      {errors.titular_domicilio.message}
                    </p>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--sm)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Documento de Identidad (DNI/CE) *
                  </label>
                  <input
                    type="text"
                    {...register('titular_documento')}
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #D1D5DB',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--font-size-body)',
                      fontFamily: 'var(--font-ui)'
                    }}
                  />
                  {errors.titular_documento && (
                    <p style={{ fontSize: 'var(--sm)', color: '#ef4444', marginTop: '0.25rem' }}>
                      {errors.titular_documento.message}
                    </p>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--sm)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Sube tu DNI/CE
                  </label>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => setTitularDNIFile(e.target.files?.[0])}
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #D1D5DB',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--font-size-body)',
                      fontFamily: 'var(--font-ui)'
                    }}
                  />
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      {...register('es_representante')}
                      style={{
                        width: '16px',
                        height: '16px',
                        marginRight: '0.5rem'
                      }}
                    />
                    <span style={{ fontSize: 'var(--sm)', fontWeight: '500' }}>
                      ¿Es representante legal?
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Sección 2: Datos del Representante (condicional) */}
            {esRepresentante && (
              <div className="libro-card" style={{ marginBottom: '2rem' }}>
                <h2 style={{
                  fontSize: 'var(--font-size-h2)',
                  fontWeight: '700',
                  fontFamily: 'var(--font-display)',
                  color: '#111827',
                  marginBottom: '0.5rem'
                }}>Datos del Representante del Titular</h2>
                <p style={{
                  fontSize: 'var(--sm)',
                  fontFamily: 'var(--font-ui)',
                  color: '#6B7280',
                  marginBottom: '1.5rem'
                }}>
                  El presente formulario deberá ser contestado únicamente por el representante legal del titular.
                </p>
                
                <div className="arco-grid-2col">
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: 'var(--sm)',
                      fontWeight: '500',
                      fontFamily: 'var(--font-ui)',
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Nombres *
                    </label>
                    <input
                      type="text"
                      {...register('representante_nombres')}
                      style={{
                        width: '100%',
                        padding: '0.5rem 0.75rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: 'var(--radius-md)',
                        fontSize: 'var(--font-size-body)',
                        fontFamily: 'var(--font-ui)'
                      }}
                    />
                    {errors.representante_nombres && (
                      <p style={{ fontSize: 'var(--sm)', color: '#ef4444', marginTop: '0.25rem' }}>
                        {errors.representante_nombres.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: 'var(--sm)',
                      fontWeight: '500',
                      fontFamily: 'var(--font-ui)',
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Apellidos *
                    </label>
                    <input
                      type="text"
                      {...register('representante_apellidos')}
                      style={{
                        width: '100%',
                        padding: '0.5rem 0.75rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: 'var(--radius-md)',
                        fontSize: 'var(--font-size-body)',
                        fontFamily: 'var(--font-ui)'
                      }}
                    />
                    {errors.representante_apellidos && (
                      <p style={{ fontSize: 'var(--sm)', color: '#ef4444', marginTop: '0.25rem' }}>
                        {errors.representante_apellidos.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: 'var(--sm)',
                      fontWeight: '500',
                      fontFamily: 'var(--font-ui)',
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Documento de Identidad (DNI/CE) *
                    </label>
                    <input
                      type="text"
                      {...register('representante_documento')}
                      style={{
                        width: '100%',
                        padding: '0.5rem 0.75rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: 'var(--radius-md)',
                        fontSize: 'var(--font-size-body)',
                        fontFamily: 'var(--font-ui)'
                      }}
                    />
                    {errors.representante_documento && (
                      <p style={{ fontSize: 'var(--sm)', color: '#ef4444', marginTop: '0.25rem' }}>
                        {errors.representante_documento.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: 'var(--sm)',
                      fontWeight: '500',
                      fontFamily: 'var(--font-ui)',
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Sube tu DNI/CE
                    </label>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => setRepresentanteDNIFile(e.target.files?.[0])}
                      style={{
                        width: '100%',
                        padding: '0.5rem 0.75rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: 'var(--radius-md)',
                        fontSize: 'var(--font-size-body)',
                        fontFamily: 'var(--font-ui)'
                      }}
                    />
                  </div>

                  <div className="arco-full-width">
                    <label style={{
                      display: 'block',
                      fontSize: 'var(--sm)',
                      fontWeight: '500',
                      fontFamily: 'var(--font-ui)',
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Documento que Acredite Representación
                    </label>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => setRepresentanteAcreditacionFile(e.target.files?.[0])}
                      style={{
                        width: '100%',
                        padding: '0.5rem 0.75rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: 'var(--radius-md)',
                        fontSize: 'var(--font-size-body)',
                        fontFamily: 'var(--font-ui)'
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Sección 3: Tipo de Solicitud */}
            <div className="libro-card" style={{ marginBottom: '2rem' }}>
              <h2 style={{
                fontSize: 'var(--font-size-h2)',
                fontWeight: '700',
                fontFamily: 'var(--font-display)',
                color: '#111827',
                marginBottom: '0.5rem'
              }}>Tipo de Solicitud</h2>
              <p style={{
                fontSize: 'var(--sm)',
                fontFamily: 'var(--font-ui)',
                color: '#6B7280',
                marginBottom: '1.5rem'
              }}>
                Seleccione el tipo de solicitud que desea ingresar (solo uno)
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {[
                  { value: 'Acceso', desc: 'Acción destinada a obtener, de la Empresa, la información sobre uno mismo almacenada en el banco de datos, así como aquella referida a las condiciones y generalidades del tratamiento de dicha información.' },
                  { value: 'Rectificación', desc: 'Acción destinada a: (i) actualizar sus datos; (ii) corregir aquella información que resulte ser inexacta, errónea o falsa; y/o (iii) incluir información en el banco de datos. En todos los casos deberá adjuntar la información que sustente la procedencia de la rectificación.' },
                  { value: 'Cancelación', desc: 'Acción de supresión de información personal almacenada en un banco de datos en razón a que esta ya no es necesaria o pertinente para la finalidad para la cual fue recopilada, cuando hubiere vencido el plazo para su tratamiento o cuando haya revocado el consentimiento otorgado.' },
                  { value: 'Oposición', desc: 'Acción de impedir el tratamiento de datos personales o cese del mismo, cuando el titular no hubiere prestado su consentimiento o cuando acredite que medien motivos fundados y legítimos relativos a una concreta situación personal que lo justifiquen.' }
                ].map((tipo) => (
                  <label key={tipo.value} style={{
                    display: 'block',
                    padding: '1rem',
                    border: '1px solid #E5E7EB',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#F9FAFB'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                  >
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      <input
                        type="radio"
                        value={tipo.value}
                        {...register('tipo_solicitud')}
                        style={{ marginTop: '0.25rem' }}
                      />
                      <div>
                        <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>{tipo.value}</div>
                        <div style={{ fontSize: 'var(--sm)', color: '#6B7280' }}>{tipo.desc}</div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {errors.tipo_solicitud && (
                <p style={{ fontSize: 'var(--sm)', color: '#ef4444', marginTop: '0.5rem' }}>
                  {errors.tipo_solicitud.message}
                </p>
              )}

              <div style={{ marginTop: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--sm)',
                  fontWeight: '500',
                  fontFamily: 'var(--font-ui)',
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  Exponga de forma clara y precisa los alcances de su solicitud *
                </label>
                <textarea
                  {...register('detalle_solicitud')}
                  rows={6}
                  placeholder="Señale la información a la que desea acceder, rectificar, cancelar o respecto de la cual desea formular oposición"
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #D1D5DB',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-body)',
                    fontFamily: 'var(--font-ui)',
                    resize: 'vertical'
                  }}
                />
                {errors.detalle_solicitud && (
                  <p style={{ fontSize: 'var(--sm)', color: '#ef4444', marginTop: '0.5rem' }}>
                    {errors.detalle_solicitud.message}
                  </p>
                )}
              </div>

              <div style={{ marginTop: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--sm)',
                  fontWeight: '500',
                  fontFamily: 'var(--font-ui)',
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  Documentos Adicionales (opcional)
                </label>
                <p style={{
                  fontSize: 'var(--sm)',
                  color: '#6B7280',
                  marginBottom: '0.5rem'
                }}>
                  Si cuenta con información adicional y/o documentos que faciliten la localización de los datos personales, por favor adjuntar copia de estos.
                </p>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => setDocumentosAdicionalesFile(e.target.files?.[0])}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #D1D5DB',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-body)',
                    fontFamily: 'var(--font-ui)'
                  }}
                />
              </div>
            </div>

            {/* Aviso Legal */}
            <div style={{
              background: '#FEF3C7',
              border: '1px solid #FDE047',
              borderRadius: 'var(--radius-md)',
              padding: '1.5rem',
              marginBottom: '2rem'
            }}>
              <p style={{
                fontSize: 'var(--sm)',
                fontFamily: 'var(--font-ui)',
                color: '#78350F',
                lineHeight: '1.6',
                margin: 0
              }}>
                PLAZA AURORA procederá a notificar la respuesta a su solicitud a la dirección de correo electrónico indicado en el presente formulario. El titular de datos personales autoriza y acepta la remisión de las notificaciones referidas a este procedimiento a dicha dirección. Las solicitudes se responderán dentro de los plazos establecidos en el Capítulo I del Título IV del Reglamento de la Ley de Protección de Datos Personales, aprobado por Decreto Supremo No. 003-2013-JUS. Si transcurren los plazos antes indicados sin haber recibido respuesta, usted podrá considerar denegada su solicitud, quedando a salvo su derecho de iniciar un procedimiento de tutela ante la Dirección General de Protección de Datos Personales (Ministerio de Justicia). Adicionalmente, si desea ser notificado a su domicilio deberá enviar su solicitud firmada vía carta indicando su nombre completo, tipo de solicitud, domicilio, fecha y copia de documento de identidad, dirigida a GRUPO SOLHER S.A.
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.875rem 2rem',
                background: loading ? '#9CA3AF' : 'var(--ctaBg)',
                color: 'var(--ctaText)',
                border: 'none',
                borderRadius: 'var(--radius-pill)',
                fontSize: 'var(--font-size-body)',
                fontWeight: '600',
                fontFamily: 'var(--font-ui)',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(-2px)'
                  e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                  e.currentTarget.style.background = 'var(--brand-blue)'
                  e.currentTarget.style.color = 'white'
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = 'none'
                  e.currentTarget.style.background = 'var(--ctaBg)'
                  e.currentTarget.style.color = 'var(--ctaText)'
                }
              }}
              onMouseDown={(e) => {
                if (!loading) {
                  e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-400)'
                  e.currentTarget.style.background = 'var(--brand-blue-600)'
                }
              }}
            >
              {loading ? 'Enviando solicitud...' : 'Enviar Solicitud'}
            </button>
          </form>
        </div>
      </div>
    </>
  )
}

